import { Tooltip, UIText3 } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import styles from './VillageInfo.css';
import { getTooltipItem } from './getTooltipItem';
import { getHighways } from './highway_helpers';
import { getStations } from './stations_helpers';
import { NBSP } from '../../constants';
import { TVillageOffersType } from '../../constants/base';
import { ObjectsDisclaimerContainer } from '../../containers/ObjectsDisclaimer';
import { PrintPhotoContainer } from '../../containers/PrintPhoto';
import { VILLAGE_CLASS_NAMES } from '../../interfaces/village';
import { IVillageSchema } from '../../types';
import { getCounterSetOnSale, getYearRange } from '../../utils/helpers';
import { isResaleOnlyOrNoOffers } from '../../utils/isResaleOnlyOrNoOffers';
import { getMicrodata } from '../../utils/microdata';
import { BlockErrorBoundary } from '../BlockErrorBoundary';
import { Gallery } from '../Gallery';
import { InfoIcon } from '../common/icons';
import { NavigationContainer } from '../navigation/container';

export interface IVillageInfoProps {
  village: IVillageSchema;
  regionHasRoads: boolean;
  regionHasStations: boolean;
  isFatCard: boolean;
  isCroppedCard: boolean;
  offersType: TVillageOffersType | undefined;
}

export const VillageInfo = (props: IVillageInfoProps) => {
  if (!props.village) {
    return null;
  }

  const { regionHasRoads, regionHasStations, village, isFatCard, isCroppedCard, offersType } = props;
  const {
    counterSet = {},
    geo = {},
    name,
    finishYearMin,
    finishYearMax,
    villageClass,
    roadInfoList = [],
    stationInfoList = [],
  } = village;

  const {
    onSaleCottage = 0,
    onSaleLandplot = 0,
    onSaleLowrisehouse = 0,
    onSaleTownhouse = 0,
    onSaleHouseShare = 0,
  } = counterSet;
  const totalOnSale = getCounterSetOnSale(counterSet);

  const region = geo.address?.details?.[0];

  const address = geo.address?.details?.map((addr, index) => {
    const url = addr.seo ? addr.seo.url : '';

    const addressItem = (
      <a className={styles['address-link']} href={url} key={index} target="_blank">
        {addr.fullName}
      </a>
    );

    return [index > 0 && ', ', addressItem];
  });

  const highways = getHighways(regionHasRoads, roadInfoList, region?.id);
  const stations = getStations(regionHasStations, stationInfoList);
  const titleBlock = (
    <>
      <h1 {...mergeStyles(styles['title'], isResaleOnlyOrNoOffers(offersType) && styles['title--with-disclaimer'])}>
        Коттеджный посёлок «{name}»
      </h1>
      <ObjectsDisclaimerContainer />
    </>
  );

  const header = isFatCard ? (
    <>
      <NavigationContainer />
      {titleBlock}
    </>
  ) : (
    <>
      {titleBlock}
      <NavigationContainer />
    </>
  );

  return (
    <div
      {...mergeStyles(
        styles['info'],

        isCroppedCard && styles['info--without_summary'],
        isFatCard && styles['info--fat'],
      )}
      {...getMicrodata('Place')}
    >
      {header}
      {!isFatCard && (
        <div>
          <BlockErrorBoundary>
            <Gallery />
          </BlockErrorBoundary>
          <PrintPhotoContainer />
        </div>
      )}
      <div className={styles['address']} itemProp="address">
        {address}
      </div>
      <div className={styles['commute_set']}>
        {highways}
        {stations}
      </div>
      {!isCroppedCard && (
        <>
          {!isFatCard && <div className={styles['hr']} />}
          <div>
            {!!finishYearMin && !!finishYearMax && (
              <div className={styles['counter_set-item']}>
                <div className={styles['label']}>Год сдачи</div>
                <div className={styles['value']}>{getYearRange(finishYearMin, finishYearMax)}</div>
              </div>
            )}
            {villageClass && villageClass in VILLAGE_CLASS_NAMES && (
              <div className={styles['counter_set-item']}>
                <div className={styles['label']}>Класс</div>
                <div className={styles['value']}>{VILLAGE_CLASS_NAMES[villageClass]}</div>
              </div>
            )}
            {!!counterSet.total && (
              <div className={styles['counter_set']}>
                {!!offersType && offersType !== 'resale' && (
                  <div className={styles['counter_set-item']}>
                    <div className={styles['label']}>Объектов</div>
                    <div className={styles['value']}>{counterSet.total}</div>
                  </div>
                )}
                <div className={styles['counter_set']}>
                  {!!offersType && (
                    <div {...mergeStyles(styles['counter_set-item'], styles['counter_set-item--tooltip'])}>
                      <div className={styles['label']}>В продаже</div>
                      <div className={styles['value']}>
                        {totalOnSale}
                        <span className={styles['helper']}>
                          <Tooltip
                            theme="white"
                            placement="right"
                            title={
                              <UIText3>
                                В продаже{NBSP}
                                {getTooltipItem(onSaleCottage, 'cottage')}
                                {getTooltipItem(onSaleTownhouse, 'townhouse')}
                                {getTooltipItem(onSaleLowrisehouse, 'lowrisehouse')}
                                {getTooltipItem(onSaleLandplot, 'landplot')}
                                {getTooltipItem(onSaleHouseShare, 'houseShare', false)}
                              </UIText3>
                            }
                          >
                            <div>
                              <InfoIcon />
                            </div>
                          </Tooltip>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
